import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import {
  Bancada,
  GavetaDeControles,
  LinhaDoPainel,
  Manopla,
  PainelDeResultado,
  PainelVazio,
  ReguaDeAlvo,
} from "../componentes/BancadaDeCalculo";
import { escalaDeFaixas, escalaLinear } from "../lib/bancada";
import { useHub } from "../lib/dados";
import { rolarAte } from "../lib/rolarPara";
import {
  Alerta,
  BaseLegal,
  BotaoPrimario,
  BotaoSecundario,
  Cabecalho,
  CampoMoeda,
  CampoPercentual,
  LinhaDeCnae,
  ChipDeAnexo,
  Confirmacao,
  LinhaDeValor,
  NumeroHeroi,
  SeloDePermissao,
  TabelaRolante,
} from "../componentes/RadarSimplesUi";
import {
  CORTE_FATOR_R,
  NOME_DO_ANEXO,
  RESUMO_DO_ANEXO,
  ROMANO,
  SUBLIMITE,
  TETO_ANUAL,
  TRIBUTOS,
  NOME_DO_TRIBUTO,
  calcularAliquota,
  calcularFatorR,
  emPercentual,
  emReais,
  ordinalDaFaixa,
  type Faixa,
} from "../lib/simplesNacional";
import { PERGUNTAS, ROTULO_DA_SITUACAO, analisar } from "../lib/elegibilidadeSimples";
import { TERCEIROS_PADRAO, calcularCusto } from "../lib/custoFuncionarioSimples";
import { calcularReforma } from "../lib/reformaSimples";
import {
  buscarCnae,
  lerAberturas,
  lerCatalogo,
  lerHistorico,
  mensagemDaRecusa,
  salvarAnalise,
  apagarAnalise,
  type Analise,
  type Catalogo,
  type Cnae,
} from "../lib/radarSimples";
import RadarAbertura from "./RadarAbertura";

// O Radar Simples — a ferramenta de enquadramento e simulação do Simples
// Nacional da Pós em Rotinas do Simples Nacional (000184-000186).
//
// ---------------------------------------------------------------------------
// De onde isto veio
// ---------------------------------------------------------------------------
// Portado do app da Lovable (`radar-simples.lovable.app`) a pedido de Victor em
// 04/09/2026. O que mudou está em `docs/runbooks/radar-simples.md`; em resumo:
//
//   · `analises`, `checklist_links` e `checklist_respostas` eram legíveis por
//     QUALQUER UM com a chave anon (`using (true)`), e o Histórico da origem
//     fazia `select *` sem filtro: todo aluno via as 86 análises de 55 colegas,
//     com receita e folha de clientes deles. Aqui a RLS é de dono;
//   · NENHUMA tela da origem escrevia em `analises` — o Histórico só lia, e a
//     tela vazia mandava "gerar um relatório" num módulo que não existe no
//     código. Aqui os simuladores salvam;
//   · a coluna `vedacao` não era vedação: era a CLASSIFICAÇÃO da atividade, em
//     alerta laranja, para 1.232 dos 1.237 CNAEs PERMITIDOS;
//   · a repartição dos tributos existia duas vezes (banco e componente), e o
//     simulador da Reforma usava uma tabela por ANEXO, ignorando a faixa;
//   · os dois checklists e as três planilhas do módulo Abertura viviam em
//     `useState` e sumiam ao recarregar a página. Agora são de um CLIENTE.
//
// ---------------------------------------------------------------------------
// O CNAE ESCOLHIDO VIAJA NA QUERY, e não num contexto
// ---------------------------------------------------------------------------
// Na origem, os até 5 CNAEs selecionados moravam num React Context: recarregar a
// página perdia tudo, e não havia como mandar a análise a um colega. Aqui eles
// são `?cnae=6201-5%2F01&cnae=…`, pela mesma razão de as abas do HUB DP serem
// sub-rota: estado que deveria estar no endereço, guardado em memória, é uma
// tela que não se pode indicar a ninguém.
//
// ---------------------------------------------------------------------------
// Os segmentos
// ---------------------------------------------------------------------------
//   /ferramenta/radar-simples                    o painel
//   /ferramenta/radar-simples/cnae               a busca de atividade
//   /ferramenta/radar-simples/anexos             as tabelas dos Anexos I a V
//   /ferramenta/radar-simples/elegibilidade      os 9 critérios da LC 123
//   /ferramenta/radar-simples/fator-r            o Fator R
//   /ferramenta/radar-simples/aliquota           a alíquota efetiva e o DAS
//   /ferramenta/radar-simples/custo-funcionario  o custo de um CLT
//   /ferramenta/radar-simples/reforma            CBS + IBS x DAS de hoje
//   /ferramenta/radar-simples/abertura           os clientes em abertura
//   /ferramenta/radar-simples/abertura/<uuid>    um cliente
//   /ferramenta/radar-simples/historico          as simulações salvas
//
// As TABELAS dos anexos ganharam endereço próprio, e na origem eram um modal
// atrás de um botão numa tela. É o que o contador consulta o tempo todo, e um
// modal não se manda por link nem se deixa aberto ao lado do sistema fiscal.

export const BASE = "/ferramenta/radar-simples";

const MODULOS = [
  { seg: "cnae", nome: "Busca de CNAE", resumo: "Ache a atividade e veja anexo, Fator R, FPAS e RAT." },
  { seg: "elegibilidade", nome: "Elegibilidade", resumo: "Os critérios da LC 123/2006, um a um, com a base legal." },
  { seg: "fator-r", nome: "Fator R", resumo: "Folha ÷ receita: Anexo III ou V, e quanto falta de folha." },
  { seg: "aliquota", nome: "Alíquota efetiva", resumo: "A alíquota do mês e o DAS, por anexo." },
  { seg: "custo-funcionario", nome: "Custo de funcionário", resumo: "Encargos, provisões e benefícios de um CLT." },
  { seg: "reforma", nome: "Reforma Tributária", resumo: "CBS + IBS comparados ao que o DAS já cobra." },
  { seg: "abertura", nome: "Abertura de empresa", resumo: "Questionário do cliente, custos, honorários e checklists." },
  { seg: "anexos", nome: "Tabelas dos anexos", resumo: "Alíquotas, deduções e repartição dos 8 tributos." },
  { seg: "historico", nome: "Histórico", resumo: "As simulações que você guardou." },
];

// ---------------------------------------------------------------------------
// O despacho
// ---------------------------------------------------------------------------

export default function RadarSimples() {
  const params = useParams();
  const [query] = useSearchParams();
  const hub = useHub();

  const resto = (params["*"] ?? "").split("/").filter(Boolean);
  const secao = resto[0] ?? "";

  // O curso vem da query que `aberturaDoCard` põe no link. Sem ela (rota
  // digitada, favorito antigo), o card pertence a um curso só e é ele — a mesma
  // precedência de `cursoDeVolta`.
  const daQuery = query.get("curso");
  const doCard = useMemo(
    () =>
      hub.fase === "pronto"
        ? (hub.hub.cardsDeCurso.find((c) => c.card_slug === "radar-simples")?.por_cursos ?? [])
        : [],
    [hub],
  );
  const curso = daQuery ?? (doCard.length === 1 ? doCard[0] : null);

  const [catalogo, setCatalogo] = useState<
    { fase: "carregando" } | { fase: "pronto"; dados: Catalogo } | { fase: "recusado"; motivo: string } | { fase: "erro" }
  >({ fase: "carregando" });

  useEffect(() => {
    if (hub.fase !== "pronto") return;
    if (!curso) {
      setCatalogo({ fase: "recusado", motivo: "card-nao-encontrado" });
      return;
    }
    let vivo = true;
    lerCatalogo(curso).then((r) => {
      if (!vivo) return;
      setCatalogo(r.fase === "pronto" ? { fase: "pronto", dados: r.dados } : r);
    });
    return () => {
      vivo = false;
    };
  }, [curso, hub.fase]);

  if (hub.fase !== "pronto" || catalogo.fase === "carregando") return <Carregando />;

  if (catalogo.fase === "recusado") {
    return (
      <div className="mx-auto max-w-6xl space-y-4 px-4 sm:px-6">
        <Cabine rotulo="Ferramenta" titulo="Radar Simples" />
        <Alerta titulo="Esta ferramenta não abriu">
          {mensagemDaRecusa(catalogo.motivo)}
        </Alerta>
      </div>
    );
  }
  if (catalogo.fase === "erro") {
    return (
      <div className="mx-auto max-w-6xl space-y-4 px-4 sm:px-6">
        <Cabine rotulo="Ferramenta" titulo="Radar Simples" />
        <Alerta titulo="Não conseguimos carregar">
          Tente recarregar a página. Se continuar, avise o suporte.
        </Alerta>
      </div>
    );
  }

  const dados = catalogo.dados;
  const props = { curso: curso as string, catalogo: dados };

  // O container é DAQUI, e não de cada tela.
  //
  // A `Moldura` de `Ferramenta.tsx` não tem largura própria, e o `<Rodape />`
  // que ela desenha vive dentro de um `mx-auto max-w-6xl px-4`. Sem esta linha o
  // conteúdo encosta nas duas bordas de 1.280 px enquanto o rodapé fica centrado
  // — a página com dois alinhamentos, visível na captura de 04/09 na tabela dos
  // anexos. É a cicatriz do "Rodape solto no flex" pelo lado inverso, e o lugar
  // de resolver é o despacho: assim as dez telas e os estados de erro herdam a
  // mesma largura sem repetição.
  return <div className="mx-auto max-w-6xl px-4 sm:px-6">{telaDe(secao, resto, props)}</div>;
}

function telaDe(secao: string, resto: string[], props: Props) {
  switch (secao) {
    case "":
      return <Painel {...props} />;
    case "cnae":
      return <TelaCnae {...props} />;
    case "anexos":
      return <TelaAnexos {...props} />;
    case "elegibilidade":
      return <TelaElegibilidade {...props} />;
    case "fator-r":
      return <TelaFatorR {...props} />;
    case "aliquota":
      return <TelaAliquota {...props} />;
    case "custo-funcionario":
      return <TelaCusto {...props} />;
    case "reforma":
      return <TelaReforma {...props} />;
    case "historico":
      return <TelaHistorico {...props} />;
    case "abertura":
      return <RadarAbertura {...props} aberturaId={resto[1] ?? null} />;
    default:
      return (
        <div className="space-y-4">
          <Cabine rotulo="Ferramenta" titulo="Radar Simples" />
          <Alerta titulo="Endereço não encontrado">
            Este módulo não existe.{" "}
            <Link to={comCurso(BASE, props.curso)} className="underline">
              Voltar ao painel
            </Link>
            .
          </Alerta>
        </div>
      );
  }
}

type Props = { curso: string; catalogo: Catalogo };

/** Mantém o `?curso=` ao navegar entre os módulos — senão o portão perde o curso. */
function comCurso(caminho: string, curso: string, extra?: Record<string, string[]>): string {
  const q = new URLSearchParams();
  q.set("curso", curso);
  for (const [k, vs] of Object.entries(extra ?? {})) for (const v of vs) q.append(k, v);
  return `${caminho}?${q.toString()}`;
}

// ---------------------------------------------------------------------------
// O painel
// ---------------------------------------------------------------------------

function Painel({ curso, catalogo }: Props) {
  const [contagens, setContagens] = useState<{ analises: number; clientes: number } | null>(null);
  useEffect(() => {
    let vivo = true;
    Promise.all([lerHistorico(curso, 500), lerAberturas(curso)]).then(([h, a]) => {
      if (!vivo) return;
      setContagens({
        analises: h.fase === "pronto" ? h.dados.length : 0,
        clientes: a.fase === "pronto" ? a.dados.length : 0,
      });
    });
    return () => {
      vivo = false;
    };
  }, [curso]);

  // Tile de fato só existe com dado MEDIDO — e "medido" quer dizer que ele PODE
  // ser outro. A primeira versão trazia "Anexos com tabela: 5", que é 5 desde
  // 2018 e será 5 amanhã: número constante ocupa o lugar de um fato sem dizer
  // nada, que é o mesmo defeito do zero grande da carteira vazia do Calendário.
  //
  // Os dois de baixo são do ALUNO, e por isso só aparecem quando existem.
  const fatos = [
    { rotulo: "Atividades no catálogo", valor: catalogo.faixas.length > 0 ? "1.331" : "—" },
    ...(contagens && contagens.clientes > 0
      ? [{ rotulo: "Clientes em abertura", valor: String(contagens.clientes) }]
      : []),
    ...(contagens && contagens.analises > 0
      ? [{ rotulo: "Simulações guardadas", valor: String(contagens.analises) }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <Cabine
        rotulo="Ferramenta"
        titulo="Radar Simples"
        descricao="Enquadramento, elegibilidade e simulação tributária do Simples Nacional — do CNAE ao DAS, e a abertura da empresa do começo ao fim."
        fatos={
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {fatos.map((f) => (
              <div key={f.rotulo} className="rounded-cf bg-white/10 px-3 py-2.5 backdrop-blur-sm">
                <p className="text-[11px] uppercase tracking-[0.1em] text-navy-mute">{f.rotulo}</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums text-navy-ink">{f.valor}</p>
              </div>
            ))}
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {MODULOS.map((m) => (
          <Link
            key={m.seg}
            to={comCurso(`${BASE}/${m.seg}`, curso)}
            data-modulo={m.seg}
            className="surface porta rise rounded-cf p-4"
          >
            <p className="font-semibold text-titulo">{m.nome}</p>
            <p className="mt-1 text-sm text-cinza">{m.resumo}</p>
          </Link>
        ))}
      </div>

      {/* A base legal fica; a contagem do catálogo que estava aqui saiu. Ela
          dizia "140 itens de checklist e planilha, e 30 faixas de alíquota" —
          números verdadeiros que não servem a ninguém no painel, e que a
          captura de 04/09 mostrou como uma linha de rodapé que só ocupa espaço.
          Cada tela mostra a contagem que importa PARA ELA. */}
      <BaseLegal>
        LC 123/2006 e Resolução CGSN 140/2018. As tabelas dos anexos vêm do banco do hub — a
        referência de cada parâmetro está ao lado do número, na tela que o usa.
      </BaseLegal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Os CNAEs escolhidos — o estado que viaja na query
// ---------------------------------------------------------------------------

const MAX_CNAES = 5;

function useCnaesEscolhidos(curso: string) {
  const [query, setQuery] = useSearchParams();
  const codigos = useMemo(() => query.getAll("cnae").slice(0, MAX_CNAES), [query]);
  const [cnaes, setCnaes] = useState<Cnae[]>([]);
  const [carregando, setCarregando] = useState(false);

  // Os CNAEs escolhidos chegam pelo CÓDIGO na URL e são resolvidos contra o
  // catálogo — nunca guardados inteiros na query. Assim um link antigo mostra o
  // dado de hoje, e ninguém consegue montar uma análise com descrição inventada.
  useEffect(() => {
    if (codigos.length === 0) {
      setCnaes([]);
      return;
    }
    let vivo = true;
    setCarregando(true);
    Promise.all(codigos.map((c) => buscarCnae(curso, c, 5))).then((rs) => {
      if (!vivo) return;
      const achados: Cnae[] = [];
      rs.forEach((r, i) => {
        if (r.fase !== "pronto") return;
        const exato = r.dados.cnaes.find((c) => c.codigo === codigos[i]);
        if (exato) achados.push(exato);
      });
      setCnaes(achados);
      setCarregando(false);
    });
    return () => {
      vivo = false;
    };
  }, [codigos.join("|"), curso]);

  const alternar = useCallback(
    (codigo: string) => {
      const atuais = query.getAll("cnae");
      const proximos = atuais.includes(codigo)
        ? atuais.filter((c) => c !== codigo)
        : atuais.length >= MAX_CNAES
          ? atuais
          : [...atuais, codigo];
      const nova = new URLSearchParams(query);
      nova.delete("cnae");
      for (const c of proximos) nova.append("cnae", c);
      setQuery(nova, { replace: true });
    },
    [query, setQuery],
  );

  const limpar = useCallback(() => {
    const nova = new URLSearchParams(query);
    nova.delete("cnae");
    setQuery(nova, { replace: true });
  }, [query, setQuery]);

  return { codigos, cnaes, carregando, alternar, limpar, cheio: codigos.length >= MAX_CNAES };
}

/** A faixa dos CNAEs escolhidos, mostrada no topo de cada simulador. */
function FaixaDeCnaes({
  cnaes,
  curso,
  aoRemover,
}: {
  cnaes: Cnae[];
  curso: string;
  aoRemover?: (codigo: string) => void;
}) {
  if (cnaes.length === 0) {
    return (
      <div className="rounded-cf border border-borda bg-chip px-3 py-2.5 text-sm text-cinza">
        Nenhuma atividade escolhida.{" "}
        <Link to={comCurso(`${BASE}/cnae`, curso)} className="text-link underline">
          Buscar um CNAE
        </Link>{" "}
        preenche o anexo automaticamente aqui.
      </div>
    );
  }
  return (
    <div
      data-faixa-cnaes
      className="flex flex-wrap items-center gap-2 rounded-cf border border-acento/30 bg-acento/8 px-3 py-2.5"
    >
      <span className="text-sm font-medium text-tinta">Atividades da análise:</span>
      {cnaes.map((c) => (
        <span
          key={c.codigo}
          data-cnae-escolhido={c.codigo}
          className="inline-flex items-center gap-1.5 rounded-full border border-borda bg-superficie px-2.5 py-1 text-xs"
        >
          <span className="font-mono font-semibold text-titulo">{c.codigo}</span>
          <ChipDeAnexo anexo={c.anexo_principal} />
          {aoRemover && (
            <button
              type="button"
              onClick={() => aoRemover(c.codigo)}
              aria-label={`Remover ${c.codigo}`}
              className="text-cinza hover:text-erro-tinta"
            >
              ×
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Busca de CNAE
// ---------------------------------------------------------------------------

function TelaCnae({ curso, catalogo }: Props) {
  void catalogo;
  const [termo, setTermo] = useState("");
  const [resultado, setResultado] = useState<{ total: number; cnaes: Cnae[] } | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const escolha = useCnaesEscolhidos(curso);
  const [aberto, setAberto] = useState<Cnae | null>(null);

  useEffect(() => {
    let vivo = true;
    // 250 ms de espera: a busca dispara a cada tecla e a tabela tem 1.331
    // linhas. Sem isso, digitar "consultoria" manda 11 requisições e a última a
    // chegar não é necessariamente a última digitada.
    const t = window.setTimeout(async () => {
      setBuscando(true);
      const r = await buscarCnae(curso, termo, 50);
      if (!vivo) return;
      setBuscando(false);
      if (r.fase === "pronto") {
        setResultado({ total: r.dados.total, cnaes: r.dados.cnaes });
        setErro(null);
      } else {
        setErro(r.fase === "recusado" ? mensagemDaRecusa(r.motivo) : "Não conseguimos buscar agora.");
      }
    }, 250);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [termo, curso]);

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Módulo"
        titulo="Busca de CNAE"
        descricao="Procure pelo código (com ou sem máscara) ou por palavras da atividade. A escolha aqui preenche o anexo nos simuladores."
      />

      <FaixaDeCnaes cnaes={escolha.cnaes} curso={curso} aoRemover={escolha.alternar} />
      {escolha.cnaes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Link to={comCurso(`${BASE}/aliquota`, curso, { cnae: escolha.codigos })}>
            <span className="pill-cta">Simular a alíquota</span>
          </Link>
          {escolha.cnaes.some((c) => c.exige_fator_r) && (
            <Link to={comCurso(`${BASE}/fator-r`, curso, { cnae: escolha.codigos })}>
              <span className="rounded-full border border-borda bg-superficie px-4 py-2 text-sm font-medium text-tinta">
                Calcular o Fator R
              </span>
            </Link>
          )}
          <BotaoSecundario aoClicar={escolha.limpar}>Limpar seleção</BotaoSecundario>
        </div>
      )}

      <div>
        <label htmlFor="busca-cnae" className="block text-sm font-medium text-tinta">
          Atividade ou código
        </label>
        <input
          id="busca-cnae"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="Ex.: 6201, consultoria, padaria…"
          className="mt-1.5 w-full max-w-lg rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
        />
      </div>

      {erro && <Alerta titulo="A busca não respondeu">{erro}</Alerta>}

      {resultado && (
        <>
          {/* O total DIZ quantos casaram, mesmo com a lista cortada. Lista
              truncada sem o total vira "não existe" na cabeça de quem procura. */}
          <p data-contagem className="text-sm text-cinza">
            {resultado.total === 0
              ? "Nenhuma atividade encontrada."
              : resultado.cnaes.length < resultado.total
                ? `Mostrando ${resultado.cnaes.length} de ${resultado.total} atividades. Refine a busca para ver o resto.`
                : `${resultado.total} ${resultado.total === 1 ? "atividade" : "atividades"}.`}
            {buscando && " Buscando…"}
          </p>

          {/* `divide-y` e não `gap`: numa lista densa o espaço entre itens
              vira a maior parte da altura, e o que separa uma linha da outra é
              o traço. */}
          <ul className="divide-y divide-borda rounded-cf border border-borda bg-superficie">
            {resultado.cnaes.map((c) => (
              <li key={c.codigo}>
              <LinhaDeCnae
                cnae={c}
                escolhido={escolha.codigos.includes(c.codigo)}
                aoAbrir={() => setAberto(aberto?.codigo === c.codigo ? null : c)}
                aoAlternar={
                  escolha.cheio && !escolha.codigos.includes(c.codigo)
                    ? undefined
                    : () => escolha.alternar(c.codigo)
                }
              />
              </li>
            ))}
          </ul>

          {escolha.cheio && (
            <p className="text-xs text-alerta-tinta">
              Você já escolheu {MAX_CNAES} atividades — o máximo. Remova uma para trocar.
            </p>
          )}
        </>
      )}

      {aberto && <FichaDoCnae cnae={aberto} aoFechar={() => setAberto(null)} />}
    </div>
  );
}

function FichaDoCnae({ cnae, aoFechar }: { cnae: Cnae; aoFechar: () => void }) {
  return (
    <section
      data-ficha={cnae.codigo}
      className="rounded-cf border border-acento/30 bg-superficie p-4"
      aria-labelledby="ficha-titulo"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 id="ficha-titulo" className="text-lg font-semibold text-titulo">
          <span className="font-mono text-titulo">{cnae.codigo}</span> — {cnae.descricao}
        </h2>
        <button type="button" onClick={aoFechar} className="shrink-0 text-sm text-cinza hover:text-tinta">
          Fechar
        </button>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="rounded-cf bg-chip p-3">
          <p className="text-xs text-cinza">Situação no Simples Nacional</p>
          <div className="mt-1">
            <SeloDePermissao valor={cnae.permitido_simples} />
          </div>
        </div>
        <div className="rounded-cf bg-chip p-3">
          <p className="text-xs text-cinza">Anexo</p>
          <p className="mt-0.5 font-semibold text-tinta">
            {cnae.anexo_principal ? NOME_DO_ANEXO[cnae.anexo_principal] : "não se aplica"}
          </p>
        </div>
        <div className="rounded-cf bg-chip p-3">
          <p className="text-xs text-cinza">Fator R</p>
          <p className="mt-0.5 font-semibold text-tinta">
            {cnae.exige_fator_r
              ? `Sim — com folha de ${emPercentual(CORTE_FATOR_R * 100, 0)} ou mais da receita, tributa pelo Anexo III`
              : "Não se aplica"}
          </p>
        </div>
        <div className="rounded-cf bg-chip p-3">
          <p className="text-xs text-cinza">FPAS · RAT · Grau de risco</p>
          <p className="mt-0.5 font-semibold tabular-nums text-tinta">
            {cnae.fpas ?? "—"} · {cnae.rat === null ? "—" : `${emPercentual(cnae.rat, 0)}`} ·{" "}
            {cnae.grau_risco ?? "—"}
          </p>
          {/* A procedência do dado vai JUNTO do número. Número certo sem
              procedência ensina a confiar em número velho. */}
          {cnae.fpas_rat_criterio && (
            <p className="mt-1 text-[11px] text-cinza">Origem do par: {cnae.fpas_rat_criterio}</p>
          )}
        </div>
      </div>

      {cnae.classificacao && (
        <p className="mt-3 text-sm text-cinza">
          <span className="font-medium text-tinta">Classificação:</span> {cnae.classificacao}
        </p>
      )}
      {cnae.ressalva && (
        <div className="mt-3">
          <Alerta titulo="Confira antes de enquadrar">{cnae.ressalva}</Alerta>
        </div>
      )}
      {cnae.nota_fpas_rat && <p className="mt-3 text-xs text-cinza">{cnae.nota_fpas_rat}</p>}
      {cnae.base_legal && (
        <p className="mt-3">
          <BaseLegal>{cnae.base_legal}</BaseLegal>
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// As tabelas dos anexos
// ---------------------------------------------------------------------------

function TelaAnexos({ curso, catalogo }: Props) {
  const [anexo, setAnexo] = useState(1);
  const doAnexo = catalogo.faixas.filter((f) => f.anexo === anexo).sort((a, b) => a.faixa - b.faixa);
  // Só as colunas que este anexo cobra em ALGUMA faixa. Mostrar IPI zerado no
  // Anexo III enche a tabela de traços que não informam nada.
  const tributos = TRIBUTOS.filter((t) => doAnexo.some((f) => f.reparticao[t] > 0));

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Referência"
        titulo="Tabelas dos Anexos I a V"
        descricao="Alíquota nominal, parcela a deduzir e a repartição dos tributos dentro do DAS, por faixa de receita."
      />

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Anexos">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="tab"
            aria-selected={anexo === n}
            data-anexo={n}
            onClick={() => setAnexo(n)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
              anexo === n
                ? "bg-acento text-acento-tinta"
                : "border border-borda bg-superficie text-tinta hover:border-acento/40"
            }`}
          >
            Anexo {ROMANO[n]}
          </button>
        ))}
      </div>

      <div>
        <h2 className="text-lg font-semibold text-titulo">{NOME_DO_ANEXO[anexo]}</h2>
        <p className="text-sm text-cinza">{RESUMO_DO_ANEXO[anexo]}</p>
      </div>

      <TabelaRolante rotulo={`Alíquotas do Anexo ${ROMANO[anexo]}`}>
        <Cabecalho colunas={["Faixa", "Receita bruta em 12 meses", "Alíquota nominal", "A deduzir"]} />
        <tbody>
          {doAnexo.map((f) => (
            <tr key={f.faixa} className="border-b border-borda last:border-0">
              <td className="px-3 py-2 font-medium text-tinta">{ordinalDaFaixa(f.faixa)}</td>
              <td className="px-3 py-2 text-right text-cinza tabular-nums">
                {f.receita_min === 0 ? "até " : `${emReais(f.receita_min)} a `}
                {emReais(f.receita_max)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-tinta">
                {emPercentual(f.aliquota_nominal)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-tinta">
                {f.parcela_deduzir === 0 ? "—" : emReais(f.parcela_deduzir)}
              </td>
            </tr>
          ))}
        </tbody>
      </TabelaRolante>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-titulo">Repartição dos tributos no DAS</h3>
        <TabelaRolante rotulo={`Repartição do Anexo ${ROMANO[anexo]}`}>
          <Cabecalho colunas={["Faixa", ...tributos.map((t) => NOME_DO_TRIBUTO[t])]} />
          <tbody>
            {doAnexo.map((f) => (
              <tr key={f.faixa} className="border-b border-borda last:border-0">
                <td className="px-3 py-2 font-medium text-tinta">{ordinalDaFaixa(f.faixa)}</td>
                {tributos.map((t) => (
                  <td key={t} className="px-3 py-2 text-right tabular-nums text-tinta">
                    {f.reparticao[t] === 0 ? "—" : emPercentual(f.reparticao[t])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </TabelaRolante>
      </div>

      {/* Na 6ª faixa, o tributo com traço não é "não paga": é "paga fora do
          DAS". Ausência de linha não informa nada, e um traço ao lado de números
          é lido como zero. */}
      <Alerta titulo="Na 6ª faixa">
        {anexo <= 2
          ? "O ICMS deixa de ser recolhido no DAS: a empresa passou do sublimite estadual e recolhe em guia própria."
          : "O ISS deixa de ser recolhido no DAS: a empresa passou do sublimite municipal e recolhe em guia própria."}{" "}
        O traço na tabela quer dizer isso — não que o tributo deixe de existir.
      </Alerta>
      {anexo === 4 && (
        <Alerta titulo="Anexo IV">
          A Contribuição Patronal Previdenciária não está no DAS. A empresa recolhe INSS patronal,
          RAT e terceiros em guia própria — é por isso que a coluna CPP está zerada nas seis faixas.
        </Alerta>
      )}

      <BaseLegal>LC 123/2006, art. 18 e Anexos I a V.</BaseLegal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Elegibilidade
// ---------------------------------------------------------------------------

function TelaElegibilidade({ curso }: Props) {
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [mostrou, setMostrou] = useState(false);
  const resultado = analisar(respostas);
  const responder = (id: string, valor: string) =>
    setRespostas((r) => ({ ...r, [id]: valor }));

  const pronto = resultado.faltamResponder === 0;

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Módulo"
        titulo="Elegibilidade ao Simples Nacional"
        descricao={`Os ${PERGUNTAS.length} critérios da LC 123/2006, com a base legal de cada um. Nenhum deles depende do CNAE — a vedação por atividade está na busca de CNAE.`}
      />

      <div className="space-y-4">
        {PERGUNTAS.map((p, i) => (
          <fieldset
            key={p.id}
            data-pergunta={p.id}
            className="rounded-cf border border-borda bg-superficie p-4"
          >
            {/* `<legend>` não: a especificação o posiciona SOBRE a borda, e num
                card arredondado o título sai flutuando fora da caixa. O
                `role="group"` com `aria-labelledby` dá a mesma associação ao
                leitor de tela. */}
            <div role="group" aria-labelledby={`titulo-${p.id}`}>
              <p className="text-xs uppercase tracking-wide text-cinza">
                Critério {i + 1} de {PERGUNTAS.length}
              </p>
              <h2 id={`titulo-${p.id}`} className="mt-0.5 font-semibold text-titulo">
                {p.texto}
              </h2>
              <div className="mt-3 space-y-2">
                {p.opcoes.map((o) => (
                  <label key={o.valor} className="flex items-center gap-2 text-sm text-tinta">
                    <input
                      type="radio"
                      name={p.id}
                      value={o.valor}
                      checked={respostas[p.id] === o.valor}
                      onChange={() => responder(p.id, o.valor)}
                      className="size-4 accent-acento"
                    />
                    {o.rotulo}
                  </label>
                ))}
              </div>

              {/* A subpergunta condicional. `filha` sai para uma constante
                  antes do JSX porque o TypeScript perde o estreitamento dentro
                  do callback do `.map()` — e `p.filha!` calaria o compilador
                  sem tornar o código correto. */}
              <PerguntaFilha pergunta={p} respostas={respostas} aoResponder={responder} />

              <p className="mt-3 text-xs text-cinza">📖 {p.baseLegal}</p>
            </div>
          </fieldset>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <BotaoPrimario
          desligado={!pronto}
          aoClicar={() => {
            setMostrou(true);
            // Rolar acontece no quadro SEGUINTE: a seção do resultado só existe
            // no DOM depois de `setMostrou(true)` render, e `rolarAte` num nó
            // que ainda não montou não faz nada — a cicatriz do `ref` para
            // elemento renderizado condicionalmente.
            requestAnimationFrame(() =>
              rolarAte(document.getElementById("resultado-elegibilidade")),
            );
          }}
          data-ver-resultado
        >
          Ver o resultado
        </BotaoPrimario>
        {!pronto && (
          <span className="text-sm text-cinza">
            {resultado.faltamResponder}{" "}
            {resultado.faltamResponder === 1 ? "critério ainda sem resposta" : "critérios ainda sem resposta"}.
          </span>
        )}
        {mostrou && (
          <BotaoSecundario
            aoClicar={() => {
              setRespostas({});
              setMostrou(false);
            }}
          >
            Recomeçar
          </BotaoSecundario>
        )}
      </div>

      {mostrou && pronto && (
        <section id="resultado-elegibilidade" data-resultado className="space-y-4">
          <h2 className="text-lg font-semibold text-titulo">Resultado</h2>
          {resultado.situacao === "pode" ? (
            <Confirmacao>
              <strong>{ROTULO_DA_SITUACAO.pode}.</strong> Com as respostas dadas, a empresa atende aos
              critérios da LC 123/2006.
            </Confirmacao>
          ) : resultado.situacao === "restricao" ? (
            <Alerta titulo={ROTULO_DA_SITUACAO.restricao}>
              Nenhum impedimento absoluto, mas há o que resolver ou conferir antes da opção.
            </Alerta>
          ) : (
            <div className="rounded-cf border border-erro-borda bg-erro-fundo px-3 py-2.5 text-sm text-erro-tinta">
              <strong>{ROTULO_DA_SITUACAO.nao_pode}.</strong> Ao menos um dos critérios impede a opção.
            </div>
          )}

          {resultado.motivos.length > 0 && (
            <ul className="space-y-2">
              {resultado.motivos.map((m, i) => (
                <li
                  key={i}
                  data-motivo={m.tipo}
                  className={`rounded-cf border px-3 py-2.5 text-sm ${
                    m.tipo === "bloqueio"
                      ? "border-erro-borda bg-erro-fundo text-erro-tinta"
                      : "border-alerta-tinta/40 bg-alerta-fundo text-alerta-tinta"
                  }`}
                >
                  <p className="font-medium">
                    {m.tipo === "bloqueio" ? "Impede a opção: " : "Atenção: "}
                    {m.texto}
                  </p>
                  <p className="mt-1 text-xs opacity-90">📖 {m.baseLegal}</p>
                </li>
              ))}
            </ul>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {resultado.porte && (
              <NumeroHeroi
                rotulo="Porte"
                valor={resultado.porte}
                apoio={
                  resultado.porte === "ME"
                    ? `Faturamento até ${emReais(360000)} por ano.`
                    : `Faturamento entre ${emReais(360000.01)} e ${emReais(TETO_ANUAL)}.`
                }
              />
            )}
            {resultado.sublimite && (
              <NumeroHeroi
                rotulo="Sublimite de ICMS/ISS"
                valor={resultado.sublimite === "dentro" ? "Dentro" : "Acima"}
                tom={resultado.sublimite === "dentro" ? "sucesso" : "alerta"}
                apoio={
                  resultado.sublimite === "dentro"
                    ? `Abaixo de ${emReais(SUBLIMITE)}: todos os tributos no DAS.`
                    : `Acima de ${emReais(SUBLIMITE)}: ICMS e ISS em guia própria, e a empresa continua no Simples.`
                }
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * A subpergunta que só existe num dos ramos.
 *
 * Componente próprio porque o estreitamento de `pergunta.filha` sobrevive a uma
 * atribuição local, e não sobrevive a um callback de `.map()` dentro do JSX do
 * pai. Extrair é o conserto; `!` seria calar o compilador sobre uma coisa que
 * ele estava certo em apontar.
 */
function PerguntaFilha({
  pergunta,
  respostas,
  aoResponder,
}: {
  pergunta: (typeof PERGUNTAS)[number];
  respostas: Record<string, string>;
  aoResponder: (id: string, valor: string) => void;
}) {
  const filha = pergunta.filha;
  if (!filha || respostas[pergunta.id] !== filha.quando) return null;

  return (
    <div className="mt-3 border-l-2 border-acento pl-4" data-filha={filha.id}>
      <p className="text-sm font-medium text-tinta">{filha.texto}</p>
      <div className="mt-2 space-y-2">
        {filha.opcoes.map((o) => (
          <label key={o.valor} className="flex items-center gap-2 text-sm text-tinta">
            <input
              type="radio"
              name={filha.id}
              value={o.valor}
              checked={respostas[filha.id] === o.valor}
              onChange={() => aoResponder(filha.id, o.valor)}
              className="size-4 accent-acento"
            />
            {o.rotulo}
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fator R
// ---------------------------------------------------------------------------

function TelaFatorR({ curso }: Props) {
  const escolha = useCnaesEscolhidos(curso);
  const [receita, setReceita] = useState<number | null>(null);
  const [folha, setFolha] = useState<number | null>(null);
  const [salvou, setSalvou] = useState<string | null>(null);

  const r = receita && receita > 0 ? calcularFatorR(receita, folha ?? 0) : null;
  const resultado = r && !("erro" in r) ? r : null;

  const comFatorR = escolha.cnaes.filter((c) => c.exige_fator_r);
  const semFatorR = escolha.cnaes.length > 0 && comFatorR.length === 0;

  // A folha usa escala LINEAR limitada pela receita: folha maior que a receita
  // existe (empresa no prejuízo), mas o trilho é para explorar a região útil, e
  // o campo continua aceitando qualquer número. Sem receita ainda, um teto de
  // partida para o trilho não nascer travado em zero.
  const escalaDaFolha = escalaLinear(receita && receita > 0 ? receita : 500_000);

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Simulador"
        titulo="Fator R"
        descricao="Folha de pagamento dividida pela receita bruta, nos últimos 12 meses. Com 28% ou mais, as atividades do Anexo V passam a ser tributadas pelo Anexo III."
      />

      <FaixaDeCnaes cnaes={escolha.cnaes} curso={curso} aoRemover={escolha.alternar} />

      {semFatorR && (
        <Alerta titulo="Nenhuma das atividades escolhidas usa o Fator R">
          As atividades desta análise estão nos Anexos{" "}
          {[...new Set(escolha.cnaes.map((c) => ROMANO[c.anexo_principal ?? 0]))].join(", ")}, e o Fator
          R só decide entre o Anexo III e o V. Você pode calcular mesmo assim — o resultado é
          informativo.
        </Alerta>
      )}

      <Bancada
        fita={
          resultado
            ? {
                rotulo: "Fator R",
                valor: emPercentual(resultado.fatorR),
                apoio: resultado.alcancou ? "Anexo III" : "Anexo V",
                alcancou: resultado.alcancou,
              }
            : null
        }
        controles={
          <GavetaDeControles
            titulo="Os dois números dos últimos 12 meses"
            descricao="Arraste para explorar, ou digite o valor exato. A conta é ao vivo."
          >
            <Manopla
              id="fator-receita"
              rotulo="Receita bruta"
              valor={receita}
              aoMudar={setReceita}
              escala={escalaDeFaixas()}
              sufixoDoCampo="em 12 meses"
              dica="As marcas do trilho são as seis faixas do Simples."
            />
            <Manopla
              id="fator-folha"
              rotulo="Folha de pagamento"
              valor={folha}
              aoMudar={setFolha}
              escala={escalaDaFolha}
              sufixoDoCampo="em 12 meses"
              dica="Salários, pró-labore, 13º, férias e os encargos sobre eles."
              atalhos={
                receita && receita > 0
                  ? [
                      // O atalho que ensina: um clique põe a folha EXATA que
                      // fecha os 28%. É a pergunta que a tela existe para
                      // responder, e ela vira um gesto em vez de uma conta.
                      { rotulo: "A folha que fecha 28%", valor: Math.round(receita * CORTE_FATOR_R) },
                      { rotulo: "Zerar", valor: 0 },
                    ]
                  : undefined
              }
            />
          </GavetaDeControles>
        }
        resultado={
          resultado ? (
            <PainelDeResultado
              rotulo="Fator R"
              valor={emPercentual(resultado.fatorR)}
              tom={resultado.alcancou ? "sucesso" : "alerta"}
              apoio={
                <ReguaDeAlvo
                  atual={resultado.fatorR}
                  alvo={CORTE_FATOR_R * 100}
                  // O teto da régua é 40% e não 100%: o Fator R real vive entre
                  // 0 e ~40, e numa régua até 100 os 28% ficam no primeiro
                  // terço com todo o resto vazio — a barra deixaria de mostrar
                  // a única coisa que ela tem para mostrar, que é a distância
                  // até o corte.
                  maximo={40}
                  rotuloAlvo="28%"
                  alcancou={resultado.alcancou}
                />
              }
            >
              <LinhaDoPainel rotulo="Anexo aplicável" valor={`Anexo ${ROMANO[resultado.anexo]}`} forte />
              <LinhaDoPainel rotulo="Folha (12 meses)" valor={emReais(resultado.folha12m)} />
              <LinhaDoPainel rotulo="Receita (12 meses)" valor={emReais(resultado.rbt12)} />
              <LinhaDoPainel
                rotulo="Folha para alcançar 28%"
                valor={emReais(resultado.rbt12 * CORTE_FATOR_R)}
              />
              {/* O NÚMERO que falta, e não "considere aumentar a folha". É ele
                  que transforma o conselho em decisão. */}
              {resultado.faltaDeFolha !== null && (
                <LinhaDoPainel rotulo="Falta" valor={emReais(resultado.faltaDeFolha)} forte />
              )}
            </PainelDeResultado>
          ) : (
            <PainelVazio>
              Informe a <strong>receita bruta dos últimos 12 meses</strong> para o Fator R aparecer
              aqui. Ele acompanha a rolagem, então dá para mexer nos números e ver o resultado ao
              mesmo tempo.
            </PainelVazio>
          )
        }
      />

      {resultado && (
        <section data-resultado className="space-y-4">
          {/* A peça é escolhida pelo RAMO, e não uma peça com `tom`: alcançar
              os 28% é uma CONFIRMAÇÃO (família verde) e não alcançar é uma
              ATENÇÃO (laranja). Um componente que aceita as duas cores deixa
              escolher a errada — e a errada aqui diz "está tudo bem" para quem
              está pagando a alíquota maior. */}
          {resultado.alcancou ? (
            <Confirmacao>
              <strong className="block">
                Alcançou os 28% — as atividades do Anexo V passam ao Anexo III
              </strong>
              A folha de <strong>{emReais(resultado.folha12m)}</strong> passa dos{" "}
              <strong>{emReais(resultado.rbt12 * CORTE_FATOR_R)}</strong> que os 28% exigem sobre
              esta receita. O Anexo III tem alíquota menor que o V na maior parte das faixas.
            </Confirmacao>
          ) : (
            <Alerta titulo="Abaixo dos 28% — as atividades do Anexo V permanecem nele">
            {resultado.faltaDeFolha !== null ? (
              <>
                Com esta receita, a folha precisaria ser de{" "}
                <strong>{emReais(resultado.rbt12 * CORTE_FATOR_R)}</strong> — ou seja{" "}
                <strong>{emReais(resultado.faltaDeFolha)}</strong> a mais nos 12 meses. Pró-labore
                conta na folha.
              </>
            ) : null}
            </Alerta>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <BotaoSecundario
              data-salvar
              aoClicar={async () => {
                const r = await salvarAnalise(curso, {
                  modulo: "fator-r",
                  cnae: escolha.codigos[0] ?? null,
                  anexo: resultado.anexo,
                  receita: resultado.rbt12,
                  folha: resultado.folha12m,
                  fatorR: resultado.fatorR,
                  detalhe: {
                    alcancou: resultado.alcancou,
                    falta_de_folha: resultado.faltaDeFolha,
                    cnaes: escolha.codigos,
                  },
                });
                setSalvou(r.ok ? "guardado" : mensagemDaRecusa(r.motivo, r.mensagem));
              }}
            >
              Guardar no histórico
            </BotaoSecundario>
            {salvou === "guardado" ? (
              <span className="text-sm text-aviso-tinta">
                Guardado.{" "}
                <Link to={comCurso(`${BASE}/historico`, curso)} className="underline">
                  Ver o histórico
                </Link>
              </span>
            ) : (
              salvou && <span className="text-sm text-erro-tinta">{salvou}</span>
            )}
          </div>

          <BaseLegal>LC 123/2006, art. 18, §5º-J e §5º-M.</BaseLegal>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alíquota efetiva
// ---------------------------------------------------------------------------

type LinhaDeSimulacao = { chave: string; anexo: number | null; receitaMes: number | null };

function TelaAliquota({ curso, catalogo }: Props) {
  const escolha = useCnaesEscolhidos(curso);
  const [rbt12, setRbt12] = useState<number | null>(null);
  const [linhas, setLinhas] = useState<LinhaDeSimulacao[]>([
    { chave: "1", anexo: null, receitaMes: null },
  ]);
  const [salvou, setSalvou] = useState<string | null>(null);
  const [preencheu, setPreencheu] = useState(false);

  // Os anexos das atividades escolhidas preenchem as linhas UMA vez. Sem a
  // trava, mexer nas linhas e navegar de volta reescreveria o que o aluno fez.
  useEffect(() => {
    if (preencheu || escolha.cnaes.length === 0) return;
    const anexos = [...new Set(escolha.cnaes.map((c) => c.anexo_principal).filter((a): a is number => a !== null))];
    if (anexos.length === 0) return;
    setLinhas(anexos.map((a, i) => ({ chave: String(i + 1), anexo: a, receitaMes: null })));
    setPreencheu(true);
  }, [escolha.cnaes, preencheu]);

  const resultados = useMemo(() => {
    if (!rbt12 || rbt12 <= 0) return [];
    return linhas
      .filter((l) => l.anexo !== null && l.receitaMes !== null && l.receitaMes > 0)
      .map((l) => ({
        chave: l.chave,
        anexo: l.anexo as number,
        calculo: calcularAliquota(catalogo.faixas, l.anexo as number, rbt12, l.receitaMes as number),
      }));
  }, [linhas, rbt12, catalogo.faixas]);

  const bons = resultados.filter((r) => !("erro" in r.calculo));
  const totalDas = bons.reduce((s, r) => s + (r.calculo as { das: number }).das, 0);
  const totalFaturado = bons.reduce((s, r) => s + (r.calculo as { receitaDoMes: number }).receitaDoMes, 0);
  const acimaDoTeto = resultados.some((r) => "erro" in r.calculo && r.calculo.erro === "acima-do-teto");
  // A efetiva do CONJUNTO, e não a média das efetivas: quando a empresa fatura
  // em dois anexos, a média aritmética das duas alíquotas não é o que ela paga.
  const efetivaDoTotal = totalFaturado > 0 ? (totalDas / totalFaturado) * 100 : 0;

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Simulador"
        titulo="Alíquota efetiva e DAS"
        descricao="A alíquota sai da receita dos últimos 12 meses; o valor do DAS, do faturamento do mês. Uma linha por anexo, quando a empresa fatura em mais de um."
      />

      <FaixaDeCnaes cnaes={escolha.cnaes} curso={curso} aoRemover={escolha.alternar} />

      {acimaDoTeto && (
        <Alerta titulo="Acima do teto do Simples Nacional">
          Com {emReais(rbt12 ?? 0)} nos últimos 12 meses, a empresa passou do limite de{" "}
          {emReais(TETO_ANUAL)} e está fora do Simples. A exclusão e o regime que passa a valer
          dependem de quando o limite foi ultrapassado e de quanto.
        </Alerta>
      )}

      <Bancada
        fita={
          bons.length > 0
            ? {
                rotulo: "DAS do mês",
                valor: emReais(totalDas),
                apoio: `${emPercentual(efetivaDoTotal)} sobre ${emReais(totalFaturado)}`,
              }
            : null
        }
        controles={
          <>
            <GavetaDeControles
              titulo="A receita que define a faixa"
              descricao="Arraste para ver a alíquota subir de degrau. As marcas do trilho são as seis faixas da lei."
            >
              <Manopla
                id="rbt12"
                rotulo="Receita bruta dos últimos 12 meses (RBT12)"
                valor={rbt12}
                aoMudar={setRbt12}
                escala={escalaDeFaixas()}
                sufixoDoCampo="RBT12"
                dica="É ela que define a faixa e a alíquota — não o faturamento do mês."
              />
            </GavetaDeControles>

            <GavetaDeControles
              titulo="Faturamento do mês, por anexo"
              descricao="Uma linha por anexo, quando a empresa fatura em mais de um."
            >
              {linhas.map((l, i) => (
                <div key={l.chave} data-linha={i} className="space-y-3 border-l-2 border-borda pl-3">
                  <div className="flex items-end gap-3">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <label htmlFor={`anexo-${l.chave}`} className="block text-sm font-medium text-tinta">
                        Anexo
                      </label>
                      <select
                        id={`anexo-${l.chave}`}
                        value={l.anexo ?? ""}
                        onChange={(e) =>
                          setLinhas((atual) =>
                            atual.map((x) =>
                              x.chave === l.chave ? { ...x, anexo: e.target.value ? Number(e.target.value) : null } : x,
                            ),
                          )
                        }
                        className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento"
                      >
                        <option value="">Escolha o anexo</option>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>
                            {NOME_DO_ANEXO[n]}
                          </option>
                        ))}
                      </select>
                    </div>
                    {linhas.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setLinhas((atual) => atual.filter((x) => x.chave !== l.chave))}
                        className="mb-1 shrink-0 rounded-full border border-borda px-3 py-1.5 text-xs text-cinza hover:border-erro-borda hover:text-erro-tinta"
                      >
                        Remover
                      </button>
                    )}
                  </div>
                  <Manopla
                    id={`receita-${l.chave}`}
                    rotulo="Faturamento do mês"
                    valor={l.receitaMes}
                    aoMudar={(n) =>
                      setLinhas((atual) => atual.map((x) => (x.chave === l.chave ? { ...x, receitaMes: n } : x)))
                    }
                    // O trilho do MÊS vai até um doze avos da receita anual —
                    // que é o mês médio —, com folga de 2x para o mês forte.
                    // Usar a escala de faixas aqui poria o mês inteiro no
                    // primeiro centímetro do trilho.
                    escala={escalaLinear(rbt12 && rbt12 > 0 ? (rbt12 / 12) * 2 : 100_000)}
                    sufixoDoCampo="no mês"
                    atalhos={
                      rbt12 && rbt12 > 0
                        ? [{ rotulo: "Um doze avos da RBT12", valor: Math.round(rbt12 / 12) }]
                        : undefined
                    }
                  />
                </div>
              ))}

              <BotaoSecundario
                data-adicionar-linha
                aoClicar={() =>
                  setLinhas((l) => [...l, { chave: String(Date.now()), anexo: null, receitaMes: null }])
                }
              >
                + Outro anexo
              </BotaoSecundario>
            </GavetaDeControles>
          </>
        }
        resultado={
          bons.length > 0 ? (
            <PainelDeResultado
              rotulo={bons.length > 1 ? "DAS do mês (todos os anexos)" : "DAS do mês"}
              valor={emReais(totalDas)}
              apoio={
                <>
                  Alíquota efetiva de <strong>{emPercentual(efetivaDoTotal)}</strong> sobre{" "}
                  {emReais(totalFaturado)} faturados.
                </>
              }
            >
              {bons.map((r) => {
                const c = r.calculo as Extract<ReturnType<typeof calcularAliquota>, { das: number }>;
                return (
                  <LinhaDoPainel
                    key={r.chave}
                    rotulo={
                      <>
                        {NOME_DO_ANEXO[r.anexo]}
                        <span className="block text-xs text-cinza">
                          {ordinalDaFaixa(c.faixa.faixa)} faixa · {emPercentual(c.aliquotaEfetiva)}
                        </span>
                      </>
                    }
                    valor={emReais(c.das)}
                  />
                );
              })}
            </PainelDeResultado>
          ) : (
            <PainelVazio>
              Informe a <strong>RBT12</strong> e o <strong>faturamento do mês</strong> de ao menos um
              anexo. O DAS aparece aqui e acompanha a rolagem, então dá para arrastar a receita e ver
              a alíquota mudar de degrau.
            </PainelVazio>
          )
        }
      />

      {bons.length > 0 && (
        <section data-resultado className="space-y-4">
          {bons.map((r) => {
            const c = r.calculo as Extract<ReturnType<typeof calcularAliquota>, { das: number }>;
            return (
              <article key={r.chave} className="rounded-cf border border-borda bg-superficie p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-semibold text-titulo">{NOME_DO_ANEXO[r.anexo]}</h3>
                  <span className="text-sm text-cinza">
                    {ordinalDaFaixa(c.faixa.faixa)} faixa · nominal {emPercentual(c.faixa.aliquota_nominal)}
                    {c.faixa.parcela_deduzir > 0 && ` · deduz ${emReais(c.faixa.parcela_deduzir)}`}
                  </span>
                </div>

                <details className="mt-3" open>
                  <summary className="cursor-pointer text-sm font-medium text-link">
                    A composição do DAS
                  </summary>
                  <div className="mt-2" data-composicao={r.anexo}>
                    {TRIBUTOS.filter((t) => c.faixa.reparticao[t] > 0 || c.tributosForaDoDas.includes(t)).map(
                      (t) => (
                        <LinhaDeValor
                          key={t}
                          rotulo={NOME_DO_TRIBUTO[t]}
                          valor={emReais(c.composicao[t])}
                          apagada={c.tributosForaDoDas.includes(t)}
                          nota={c.tributosForaDoDas.includes(t) ? "fora do DAS" : undefined}
                        />
                      ),
                    )}
                    <LinhaDeValor rotulo="Total" valor={emReais(c.das)} forte />
                  </div>
                  {c.tributosForaDoDas.length > 0 && (
                    <p className="mt-2 text-xs text-alerta-tinta">
                      Na 6ª faixa, {c.tributosForaDoDas.map((t) => NOME_DO_TRIBUTO[t]).join(" e ")} não
                      {c.tributosForaDoDas.length > 1 ? " são recolhidos" : " é recolhido"} no DAS — a guia
                      é própria.
                    </p>
                  )}
                </details>
              </article>
            );
          })}

          <div className="flex flex-wrap items-center gap-3">
            <BotaoSecundario
              data-salvar
              aoClicar={async () => {
                const primeiro = bons[0].calculo as Extract<ReturnType<typeof calcularAliquota>, { das: number }>;
                const r = await salvarAnalise(curso, {
                  modulo: "aliquota",
                  cnae: escolha.codigos[0] ?? null,
                  anexo: bons[0].anexo,
                  receita: rbt12,
                  aliquota: primeiro.aliquotaEfetiva,
                  das: totalDas,
                  detalhe: {
                    linhas: bons.map((b) => ({
                      anexo: b.anexo,
                      faixa: (b.calculo as { faixa: Faixa }).faixa.faixa,
                      das: (b.calculo as { das: number }).das,
                    })),
                    cnaes: escolha.codigos,
                  },
                });
                setSalvou(r.ok ? "guardado" : mensagemDaRecusa(r.motivo, r.mensagem));
              }}
            >
              Guardar no histórico
            </BotaoSecundario>
            {salvou === "guardado" ? (
              <span className="text-sm text-aviso-tinta">Guardado.</span>
            ) : (
              salvou && <span className="text-sm text-erro-tinta">{salvou}</span>
            )}
            <Link
              to={comCurso(`${BASE}/reforma`, curso, { cnae: escolha.codigos })}
              className="text-sm text-link underline"
            >
              Comparar com a Reforma Tributária
            </Link>
          </div>

          <BaseLegal>
            LC 123/2006, art. 18. (RBT12 × alíquota nominal − parcela a deduzir) ÷ RBT12. Valores
            estimados.
          </BaseLegal>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custo de funcionário
// ---------------------------------------------------------------------------

function TelaCusto({ curso, catalogo }: Props) {
  const escolha = useCnaesEscolhidos(curso);
  const [salario, setSalario] = useState<number | null>(null);
  const [anexo, setAnexo] = useState(3);
  const [rat, setRat] = useState(1);
  const [terceiros, setTerceiros] = useState(
    catalogo.parametros.terceiros_percentual?.valor ?? TERCEIROS_PADRAO,
  );
  const [sst, setSst] = useState<number | null>(null);
  const [vt, setVt] = useState<number | null>(null);
  const [va, setVa] = useState<number | null>(null);
  const [plano, setPlano] = useState<number | null>(null);
  const [outros, setOutros] = useState<number | null>(null);
  const [seguiuCnae, setSeguiuCnae] = useState(false);

  // O CNAE escolhido traz o RAT e o anexo dele — mas só sugere, uma vez. O RAT
  // real depende do estabelecimento, e o contador pode ter outro na mão.
  useEffect(() => {
    if (seguiuCnae || escolha.cnaes.length === 0) return;
    const c = escolha.cnaes[0];
    if (c.anexo_principal) setAnexo(c.anexo_principal);
    if (c.rat !== null && c.rat > 0) setRat(c.rat);
    setSeguiuCnae(true);
  }, [escolha.cnaes, seguiuCnae]);

  const r = calcularCusto({
    salario: salario ?? 0,
    anexo,
    ratPercentual: rat,
    terceirosPercentual: terceiros,
    sst: sst ?? 0,
    valeTransporte: vt ?? 0,
    valeAlimentacao: va ?? 0,
    planoSaude: plano ?? 0,
    outrosBeneficios: outros ?? 0,
  });
  const temSalario = (salario ?? 0) > 0;

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Simulador"
        titulo="Custo de um funcionário CLT"
        descricao="Encargos do mês, provisões, SST e benefícios. No Simples, a contribuição patronal só sai do DAS no Anexo IV."
      />

      <FaixaDeCnaes cnaes={escolha.cnaes} curso={curso} aoRemover={escolha.alternar} />

      <Bancada
        fita={
          temSalario
            ? {
                rotulo: "Custo total no mês",
                valor: emReais(r.custoTotal),
                apoio: `${emPercentual(r.custoPercentual - 100, 1)} além do salário`,
              }
            : null
        }
        controles={
          <>
            <GavetaDeControles
              titulo="O salário e o regime"
              descricao="Arraste o salário para ver o custo total acompanhar."
            >
              <Manopla
                id="salario"
                rotulo="Salário bruto mensal"
                valor={salario}
                aoMudar={setSalario}
                // Faixa útil de um CLT. Quem paga mais que isso digita no campo,
                // que continua aceitando qualquer valor.
                escala={escalaLinear(30_000)}
                sufixoDoCampo="por mês"
              />

              <div className="space-y-1.5">
                <label htmlFor="anexo-custo" className="block text-sm font-medium text-tinta">
                  Anexo do Simples Nacional
                </label>
                <select
                  id="anexo-custo"
                  value={anexo}
                  onChange={(e) => setAnexo(Number(e.target.value))}
                  className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {NOME_DO_ANEXO[n]}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-cinza">
                  Troque para o Anexo IV e veja o custo saltar — é o único em que a contribuição
                  patronal sai do DAS.
                </p>
              </div>

              {anexo === 4 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label htmlFor="rat" className="block text-sm font-medium text-tinta">
                      RAT — grau de risco
                    </label>
                    <select
                      id="rat"
                      value={rat}
                      onChange={(e) => setRat(Number(e.target.value))}
                      className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento"
                    >
                      <option value={1}>1% — risco leve</option>
                      <option value={2}>2% — risco médio</option>
                      <option value={3}>3% — risco grave</option>
                    </select>
                    {escolha.cnaes[0]?.rat != null && (
                      <p className="text-xs text-cinza">
                        O CNAE {escolha.cnaes[0].codigo} tem RAT de{" "}
                        {emPercentual(escolha.cnaes[0].rat, 0)} no catálogo.
                      </p>
                    )}
                  </div>
                  <CampoPercentual
                    id="terceiros"
                    rotulo="Contribuição a terceiros"
                    valor={terceiros}
                    aoMudar={setTerceiros}
                    dica={catalogo.parametros.terceiros_percentual?.referencia}
                  />
                </div>
              )}
            </GavetaDeControles>

            <GavetaDeControles
              titulo="SST e benefícios"
              descricao="Opcionais — mas o PGR e o PCMSO são obrigatórios para quem tem empregado CLT, mesmo no Simples."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <CampoMoeda id="sst" rotulo="SST por mês" valor={sst} aoMudar={setSst} />
                <CampoMoeda id="vt" rotulo="Vale-transporte" valor={vt} aoMudar={setVt} />
                <CampoMoeda id="va" rotulo="Vale-alimentação" valor={va} aoMudar={setVa} />
                <CampoMoeda id="plano" rotulo="Plano de saúde" valor={plano} aoMudar={setPlano} />
                <CampoMoeda id="outros" rotulo="Outros benefícios" valor={outros} aoMudar={setOutros} />
              </div>
            </GavetaDeControles>
          </>
        }
        resultado={
          temSalario ? (
            <PainelDeResultado
              rotulo="Custo total no mês"
              valor={emReais(r.custoTotal)}
              apoio={
                <>
                  <strong>{emPercentual(r.custoPercentual - 100, 1)}</strong> além do próprio salário
                  · {emReais(r.custoTotal * 12)} no ano.
                </>
              }
              aviso={
                r.quantoOAnexoIvCustariaAMais !== null ? (
                  <>
                    No <strong>Anexo IV</strong> este mesmo salário custaria{" "}
                    <strong>{emReais(r.quantoOAnexoIvCustariaAMais)}</strong> a mais por mês.
                  </>
                ) : undefined
              }
            >
              <LinhaDoPainel rotulo="Salário bruto" valor={emReais(r.salario)} />
              <LinhaDoPainel
                rotulo={r.patronalForaDoDas ? "INSS + RAT + terceiros" : "INSS + RAT + terceiros (no DAS)"}
                valor={
                  r.patronalForaDoDas ? emReais(r.inssPatronal + r.rat + r.terceiros) : emReais(0)
                }
              />
              <LinhaDoPainel rotulo="FGTS (8%)" valor={emReais(r.fgts)} />
              {r.sst > 0 && <LinhaDoPainel rotulo="SST" valor={emReais(r.sst)} />}
              {r.beneficios > 0 && <LinhaDoPainel rotulo="Benefícios" valor={emReais(r.beneficios)} />}
              <LinhaDoPainel
                rotulo="Provisões (13º, férias, multa)"
                valor={emReais(r.provisao13 + r.provisaoFerias + r.provisaoMultaFgts)}
              />
              <LinhaDoPainel rotulo="Custo total" valor={emReais(r.custoTotal)} forte />
            </PainelDeResultado>
          ) : (
            <PainelVazio>
              Informe o <strong>salário bruto</strong> para o custo aparecer aqui. Ele acompanha a
              rolagem — dá para arrastar o salário e trocar o anexo vendo o total mudar.
            </PainelVazio>
          )
        }
      />

      {temSalario && (
        <section data-resultado className="space-y-4">
          <div className="rounded-cf border border-borda bg-superficie p-4">
            <h3 className="text-sm font-semibold text-titulo">Desembolso do mês</h3>
            <LinhaDeValor rotulo="Salário bruto" valor={emReais(r.salario)} />
            <LinhaDeValor
              rotulo="INSS patronal (20%)"
              valor={emReais(r.inssPatronal)}
              apagada={!r.patronalForaDoDas}
              nota={r.patronalForaDoDas ? undefined : "dentro do DAS"}
            />
            <LinhaDeValor
              rotulo={`RAT (${emPercentual(rat, 0)})`}
              valor={emReais(r.rat)}
              apagada={!r.patronalForaDoDas}
              nota={r.patronalForaDoDas ? undefined : "dentro do DAS"}
            />
            <LinhaDeValor
              rotulo={`Terceiros (${emPercentual(terceiros)})`}
              valor={emReais(r.terceiros)}
              apagada={!r.patronalForaDoDas}
              nota={r.patronalForaDoDas ? undefined : "dentro do DAS"}
            />
            <LinhaDeValor rotulo="FGTS (8%)" valor={emReais(r.fgts)} />
            {r.sst > 0 && <LinhaDeValor rotulo="SST" valor={emReais(r.sst)} />}
            {r.beneficios > 0 && <LinhaDeValor rotulo="Benefícios" valor={emReais(r.beneficios)} />}

            <h3 className="mt-4 text-sm font-semibold text-titulo">
              Provisões — reserva, não desembolso do mês
            </h3>
            <LinhaDeValor rotulo="13º salário (1/12)" valor={emReais(r.provisao13)} />
            <LinhaDeValor rotulo="Férias + 1/3 (1,3333/12)" valor={emReais(r.provisaoFerias)} />
            <LinhaDeValor
              rotulo="Multa do FGTS (8% × 40%)"
              valor={emReais(r.provisaoMultaFgts)}
              nota="se houver dispensa sem justa causa"
            />

            <LinhaDeValor rotulo="Custo total" valor={emReais(r.custoTotal)} forte />
          </div>

          {/* O que a origem não dizia: quanto o Anexo IV custaria a mais. Ela
              mostrava as linhas zeradas com "Incluso no DAS" — informa que não
              paga, não quanto isso vale. */}
          {r.quantoOAnexoIvCustariaAMais !== null ? (
            <Alerta titulo="Neste anexo, a contribuição patronal já está no DAS">
              INSS patronal, RAT e terceiros não são recolhidos em guia própria. No{" "}
              <strong>Anexo IV</strong>, o mesmo salário custaria{" "}
              <strong>{emReais(r.quantoOAnexoIvCustariaAMais)}</strong> a mais por mês —{" "}
              {emReais(r.quantoOAnexoIvCustariaAMais * 12)} no ano.
            </Alerta>
          ) : (
            <Alerta titulo="Anexo IV: a contribuição patronal sai do DAS">
              INSS patronal, RAT e terceiros são recolhidos em guia própria (GPS/DARF), além do DAS.
              É a exceção do art. 18, §5º-C da LC 123/2006.
            </Alerta>
          )}

          <BaseLegal>
            LC 123/2006, art. 13 e art. 18, §5º-C · Lei 8.036/1990, art. 15 e 18 · CF, art. 7º, VIII e
            XVII.
          </BaseLegal>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reforma Tributária
// ---------------------------------------------------------------------------

function TelaReforma({ curso, catalogo }: Props) {
  const escolha = useCnaesEscolhidos(curso);
  const cbsPadrao = catalogo.parametros.cbs_percentual;
  const ibsPadrao = catalogo.parametros.ibs_percentual;

  const [rbt12, setRbt12] = useState<number | null>(null);
  const [receitaMes, setReceitaMes] = useState<number | null>(null);
  const [anexo, setAnexo] = useState<number | null>(null);
  const [folha, setFolha] = useState<number | null>(null);
  const [cbs, setCbs] = useState(cbsPadrao?.valor ?? 8.8);
  const [ibs, setIbs] = useState(ibsPadrao?.valor ?? 17.7);
  const [creditoCbs, setCreditoCbs] = useState<number | null>(null);
  const [creditoIbs, setCreditoIbs] = useState<number | null>(null);
  const [seguiuCnae, setSeguiuCnae] = useState(false);

  useEffect(() => {
    if (seguiuCnae || escolha.cnaes.length === 0) return;
    const a = escolha.cnaes[0].anexo_principal;
    if (a) setAnexo(a);
    setSeguiuCnae(true);
  }, [escolha.cnaes, seguiuCnae]);

  // O Fator R muda o anexo ANTES do cálculo — como na origem, e a tela diz.
  const fatorR =
    anexo === 5 && rbt12 && rbt12 > 0 ? calcularFatorR(rbt12, folha ?? 0) : null;
  const fr = fatorR && !("erro" in fatorR) ? fatorR : null;
  const anexoUsado = fr ? fr.anexo : anexo;

  const calculo =
    anexoUsado && rbt12 && rbt12 > 0 && receitaMes && receitaMes > 0
      ? calcularAliquota(catalogo.faixas, anexoUsado, rbt12, receitaMes)
      : null;
  const das = calculo && !("erro" in calculo) ? calculo : null;
  const reparticao = das ? das.faixa.reparticao : null;

  const comparativo =
    das && reparticao
      ? calcularReforma({
          receitaDoMes: receitaMes as number,
          das: das.das,
          reparticao,
          cbsPercentual: cbs,
          ibsPercentual: ibs,
          creditosCbs: creditoCbs ?? 0,
          creditosIbs: creditoIbs ?? 0,
        })
      : null;

  const maisCaro = comparativo ? comparativo.diferenca > 0 : false;

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Simulador"
        titulo="Reforma Tributária no Simples"
        descricao="Compara o que a empresa já paga de PIS, Cofins, ICMS e ISS dentro do DAS com o que pagaria de CBS e IBS recolhendo fora dele."
      />

      <FaixaDeCnaes cnaes={escolha.cnaes} curso={curso} aoRemover={escolha.alternar} />

      {/* O que a conta NÃO diz, dito antes do número: a empresa PODE continuar
          no DAS. Apresentar a simulação como destino inevitável assusta quem não
          precisa mudar nada. */}
      <Alerta titulo="Isto é um dos dois caminhos, não o único">
        A empresa do Simples <strong>pode continuar recolhendo CBS e IBS dentro do DAS</strong>, pelas
        alíquotas do próprio Simples. Sair do DAS é uma opção, e costuma compensar quando os clientes
        são pessoas jurídicas que aproveitam o crédito. A simulação abaixo é do cenário “fora do DAS”.
      </Alerta>

      <Bancada
        fita={
          comparativo && !comparativo.semBaseDeComparacao
            ? {
                rotulo: "Diferença por mês",
                valor: `${comparativo.diferenca > 0 ? "+" : ""}${emReais(comparativo.diferenca)}`,
                apoio: maisCaro ? "mais caro fora do DAS" : "mais barato fora do DAS",
                alcancou: !maisCaro,
              }
            : null
        }
        controles={
          <>
            <GavetaDeControles
              titulo="A empresa"
              descricao="Arraste a receita para ver a diferença mudar de faixa."
            >
              <Manopla
                id="reforma-rbt12"
                rotulo="Receita bruta em 12 meses"
                valor={rbt12}
                aoMudar={setRbt12}
                escala={escalaDeFaixas()}
                sufixoDoCampo="RBT12"
              />
              <Manopla
                id="reforma-mes"
                rotulo="Faturamento do mês"
                valor={receitaMes}
                aoMudar={setReceitaMes}
                escala={escalaLinear(rbt12 && rbt12 > 0 ? (rbt12 / 12) * 2 : 100_000)}
                sufixoDoCampo="no mês"
                atalhos={
                  rbt12 && rbt12 > 0
                    ? [{ rotulo: "Um doze avos da RBT12", valor: Math.round(rbt12 / 12) }]
                    : undefined
                }
              />
              <div className="space-y-1.5">
                <label htmlFor="reforma-anexo" className="block text-sm font-medium text-tinta">
                  Anexo do Simples Nacional
                </label>
                <select
                  id="reforma-anexo"
                  value={anexo ?? ""}
                  onChange={(e) => setAnexo(e.target.value ? Number(e.target.value) : null)}
                  className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento"
                >
                  <option value="">Escolha o anexo</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {NOME_DO_ANEXO[n]}
                    </option>
                  ))}
                </select>
              </div>
              {anexo === 5 && (
                <Manopla
                  id="reforma-folha"
                  rotulo="Folha de 12 meses (para o Fator R)"
                  valor={folha}
                  aoMudar={setFolha}
                  escala={escalaLinear(rbt12 && rbt12 > 0 ? rbt12 : 500_000)}
                  sufixoDoCampo="em 12 meses"
                />
              )}
              {fr && (
                <div className="rounded-cf border border-acento/30 bg-acento/8 px-3 py-2.5 text-sm text-tinta">
                  Fator R de <strong>{emPercentual(fr.fatorR)}</strong> —{" "}
                  {fr.alcancou
                    ? "a simulação usa o Anexo III, que é onde a empresa tributa."
                    : "abaixo dos 28%, a simulação usa o Anexo V."}
                </div>
              )}
            </GavetaDeControles>

            <GavetaDeControles
              titulo="Alíquotas e créditos"
              descricao="As alíquotas de referência são estimativa — o valor final sai de lei complementar e de resolução do Senado."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <CampoPercentual
                  id="cbs"
                  rotulo={cbsPadrao?.rotulo ?? "CBS"}
                  valor={cbs}
                  aoMudar={setCbs}
                  dica={cbsPadrao?.referencia}
                />
                <CampoPercentual
                  id="ibs"
                  rotulo={ibsPadrao?.rotulo ?? "IBS"}
                  valor={ibs}
                  aoMudar={setIbs}
                  dica={ibsPadrao?.referencia}
                />
                <CampoMoeda
                  id="credito-cbs"
                  rotulo="Créditos de CBS no mês"
                  valor={creditoCbs}
                  aoMudar={setCreditoCbs}
                  dica="CBS destacada nas compras de mercadorias e insumos."
                />
                <CampoMoeda
                  id="credito-ibs"
                  rotulo="Créditos de IBS no mês"
                  valor={creditoIbs}
                  aoMudar={setCreditoIbs}
                />
              </div>
            </GavetaDeControles>
          </>
        }
        resultado={
          comparativo && das ? (
            comparativo.semBaseDeComparacao ? (
              <PainelVazio>
                Neste mês não há PIS, Cofins, ICMS nem ISS dentro do DAS, então{" "}
                <strong>não há o que comparar</strong>. A reforma substitui só esses quatro.
              </PainelVazio>
            ) : (
              <PainelDeResultado
                rotulo="Diferença estimada por mês"
                valor={`${comparativo.diferenca > 0 ? "+" : ""}${emReais(comparativo.diferenca)}`}
                tom={maisCaro ? "alerta" : "sucesso"}
                apoio={
                  comparativo.diferencaPercentual === null ? undefined : (
                    <>
                      {comparativo.diferencaPercentual > 0 ? "+" : ""}
                      {emPercentual(comparativo.diferencaPercentual, 1)} sobre o que já é pago hoje —{" "}
                      {maisCaro ? "sair do DAS sairia mais caro" : "sair do DAS sairia mais barato"}.
                    </>
                  )
                }
              >
                <LinhaDoPainel
                  rotulo={`Hoje — ${NOME_DO_ANEXO[das.faixa.anexo]}, ${ordinalDaFaixa(das.faixa.faixa)} faixa`}
                  valor={emReais(comparativo.substituidoHoje)}
                  forte
                />
                <LinhaDoPainel rotulo="PIS + Cofins" valor={emReais(comparativo.pisCofinsHoje)} />
                <LinhaDoPainel rotulo="ICMS + ISS" valor={emReais(comparativo.icmsIssHoje)} />
                <LinhaDoPainel rotulo="Com a reforma, fora do DAS" valor={emReais(comparativo.totalReforma)} forte />
                <LinhaDoPainel rotulo={`CBS (${emPercentual(cbs)})`} valor={emReais(comparativo.cbs)} />
                <LinhaDoPainel rotulo={`IBS (${emPercentual(ibs)})`} valor={emReais(comparativo.ibs)} />
              </PainelDeResultado>
            )
          ) : (
            <PainelVazio>
              Informe a <strong>RBT12</strong>, o <strong>faturamento do mês</strong> e o{" "}
              <strong>anexo</strong>. A comparação aparece aqui e acompanha a rolagem.
            </PainelVazio>
          )
        }
      />

      {calculo && "erro" in calculo && (
        <Alerta titulo="Não foi possível calcular">
          {calculo.erro === "acima-do-teto"
            ? `Com ${emReais(rbt12 ?? 0)} nos últimos 12 meses, a empresa passou do teto de ${emReais(TETO_ANUAL)} e está fora do Simples Nacional.`
            : "Confira a receita e o anexo escolhidos."}
        </Alerta>
      )}

      {das && comparativo && (
        <section data-resultado className="space-y-4">
          <div className="rounded-cf border border-borda bg-superficie p-4">
            <h3 className="text-sm font-semibold text-titulo">
              Hoje — {NOME_DO_ANEXO[das.faixa.anexo]}, {ordinalDaFaixa(das.faixa.faixa)} faixa
            </h3>
            <LinhaDeValor rotulo="DAS do mês" valor={emReais(das.das)} />
            <LinhaDeValor
              rotulo="PIS + Cofins (dentro do DAS)"
              valor={emReais(comparativo.pisCofinsHoje)}
            />
            <LinhaDeValor rotulo="ICMS + ISS (dentro do DAS)" valor={emReais(comparativo.icmsIssHoje)} />
            <LinhaDeValor rotulo="Total que a reforma substitui" valor={emReais(comparativo.substituidoHoje)} forte />
            <p className="mt-2 text-xs text-cinza">
              IRPJ, CSLL, CPP e IPI continuam no DAS — a reforma não os toca, e por isso eles ficam
              fora desta comparação.
            </p>
          </div>

          <div className="rounded-cf border border-borda bg-superficie p-4">
            <h3 className="text-sm font-semibold text-titulo">Com a reforma, fora do DAS</h3>
            <LinhaDeValor rotulo={`CBS (${emPercentual(cbs)})`} valor={emReais(comparativo.cbs)} />
            {comparativo.creditosCbs > 0 && (
              <LinhaDeValor rotulo="Créditos de CBS" valor={`− ${emReais(comparativo.creditosCbs)}`} />
            )}
            <LinhaDeValor rotulo={`IBS (${emPercentual(ibs)})`} valor={emReais(comparativo.ibs)} />
            {comparativo.creditosIbs > 0 && (
              <LinhaDeValor rotulo="Créditos de IBS" valor={`− ${emReais(comparativo.creditosIbs)}`} />
            )}
            <LinhaDeValor rotulo="Total CBS + IBS" valor={emReais(comparativo.totalReforma)} forte />
            {(comparativo.creditosCbs > 0 || comparativo.creditosIbs > 0) && (
              <p className="mt-2 text-xs text-cinza">
                Crédito que sobra não abate o outro tributo — CBS é federal e IBS é
                estadual/municipal — nem vira valor a receber: fica como saldo credor para o mês
                seguinte.
              </p>
            )}
          </div>

          <BaseLegal>
            EC 132/2023 · LC 214/2025 · LC 123/2006, art. 18. Alíquotas de referência estimadas — o
            valor final sai de lei complementar e de resolução do Senado, e a transição corre de 2026
            a 2033.
          </BaseLegal>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Histórico
// ---------------------------------------------------------------------------

const NOME_DO_MODULO: Record<Analise["modulo"], string> = {
  "fator-r": "Fator R",
  aliquota: "Alíquota efetiva",
  reforma: "Reforma Tributária",
};

function TelaHistorico({ curso }: Props) {
  const [estado, setEstado] = useState<
    { fase: "carregando" } | { fase: "pronto"; analises: Analise[] } | { fase: "erro" }
  >({ fase: "carregando" });

  const recarregar = useCallback(async () => {
    const r = await lerHistorico(curso, 200);
    setEstado(r.fase === "pronto" ? { fase: "pronto", analises: r.dados } : { fase: "erro" });
  }, [curso]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Módulo"
        titulo="Histórico de simulações"
        descricao="O que você guardou nos simuladores. Só você vê estas linhas."
      />

      {estado.fase === "carregando" && <Carregando />}
      {estado.fase === "erro" && <Alerta titulo="Não conseguimos carregar">Tente recarregar a página.</Alerta>}

      {estado.fase === "pronto" && estado.analises.length === 0 && (
        <div className="rounded-cf border border-borda bg-chip px-4 py-6 text-center">
          <p className="text-sm text-cinza">
            Nada guardado ainda. Nos simuladores de{" "}
            <Link to={comCurso(`${BASE}/fator-r`, curso)} className="text-link underline">
              Fator R
            </Link>{" "}
            e{" "}
            <Link to={comCurso(`${BASE}/aliquota`, curso)} className="text-link underline">
              alíquota efetiva
            </Link>{" "}
            há o botão “Guardar no histórico”.
          </p>
        </div>
      )}

      {estado.fase === "pronto" && estado.analises.length > 0 && (
        <ul className="space-y-3" data-historico>
          {estado.analises.map((a) => (
            <li
              key={a.id}
              data-analise={a.id}
              className="rounded-cf border border-borda bg-superficie p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-titulo">
                    {NOME_DO_MODULO[a.modulo] ?? a.modulo}
                    {a.cnae_codigo && (
                      <span className="ml-2 font-mono text-sm text-titulo">{a.cnae_codigo}</span>
                    )}
                  </p>
                  {a.cnae_descricao && <p className="text-sm text-cinza">{a.cnae_descricao}</p>}
                </div>
                <time className="shrink-0 text-xs text-cinza" dateTime={a.criado_em}>
                  {new Date(a.criado_em).toLocaleDateString("pt-BR")}
                </time>
              </div>

              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-cinza">
                {a.anexo_aplicavel && <span>Anexo {ROMANO[a.anexo_aplicavel]}</span>}
                {a.receita_bruta_12m !== null && <span>RBT12 {emReais(a.receita_bruta_12m)}</span>}
                {a.folha_12m !== null && <span>Folha {emReais(a.folha_12m)}</span>}
                {a.fator_r !== null && <span>Fator R {emPercentual(a.fator_r)}</span>}
                {a.aliquota_efetiva !== null && <span>Alíquota {emPercentual(a.aliquota_efetiva)}</span>}
                {a.das_mensal !== null && <span>DAS {emReais(a.das_mensal)}</span>}
              </div>

              {/* As 86 linhas migradas da Lovable dizem de onde vêm: elas foram
                  gravadas por uma versão que não registrava o módulo, e a
                  classificação delas é uma INFERÊNCIA. Dizer isso é o que separa
                  "o dado é assim" de "eu supus". */}
              {(a.detalhe as { migrado_da_lovable?: boolean } | null)?.migrado_da_lovable && (
                <p className="mt-2 text-xs text-cinza">
                  Guardado na versão anterior da ferramenta. O módulo foi inferido dos campos
                  preenchidos.
                </p>
              )}

              <button
                type="button"
                data-apagar={a.id}
                onClick={async () => {
                  const r = await apagarAnalise(curso, a.id);
                  if (r.ok) void recarregar();
                }}
                className="mt-3 text-xs text-cinza underline hover:text-erro-tinta"
              >
                Apagar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
